import type { Card, Etapa, Flow } from '../types';

/**
 * Utilitários para gerenciamento de hierarquia de cards (sistema pai/filho)
 */

/**
 * Verifica se um card pode se tornar um card pai baseado nas regras da etapa
 */
export const podeVirarCardPai = (
  card: Card,
  etapa: Etapa,
  flow: Flow
): { permitido: boolean; motivo?: string } => {
  // Verificar se a etapa permite cards pai
  if (!etapa.configuracaoHierarquia?.permitirCardsPai) {
    return {
      permitido: false,
      motivo: 'Esta etapa não permite cards pai'
    };
  }

  // Verificar se o card já é um filho de outro card
  if (card.cardPaiId) {
    return {
      permitido: false,
      motivo: 'Card filho não pode se tornar card pai'
    };
  }

  // Verificar se o card já é um pai
  if (card.ehCardPai) {
    return {
      permitido: true,
      motivo: 'Card já é um card pai'
    };
  }

  // Verificar regras de transformação por etapas permitidas
  const regras = etapa.configuracaoHierarquia.regrasTransformacao;
  if (regras?.etapasPermitidas && regras.etapasPermitidas.length > 0) {
    if (!regras.etapasPermitidas.includes(card.etapaId)) {
      return {
        permitido: false,
        motivo: 'Card não está em uma etapa permitida para virar pai'
      };
    }
  }

  // Verificar condições baseadas em campos do card
  if (regras?.condicoes && regras.condicoes.length > 0) {
    for (const condicao of regras.condicoes) {
      const valorCampo = card.campos[condicao.campoId];
      const validacao = validarCondicao(valorCampo, condicao.operador, condicao.valor);
      
      if (!validacao) {
        return {
          permitido: false,
          motivo: `Condição do campo não atendida`
        };
      }
    }
  }

  return { permitido: true };
};

/**
 * Verifica se um card pode se tornar filho de um card pai
 */
export const podeVirarCardFilho = (
  cardFilho: Card,
  cardPai: Card,
  etapaFilho: Etapa,
  etapaPai: Etapa,
  flow: Flow,
  automacoes?: any[]
): { permitido: boolean; motivo?: string; precisaConfigurar?: boolean } => {
  // Verificar se o card pai aceita filhos
  if (!etapaPai.configuracaoHierarquia?.regraAgrupamento?.aceitaFilhos) {
    return {
      permitido: false,
      motivo: 'O card pai não aceita filhos'
    };
  }

  // Verificar se o card pai realmente é um pai
  if (!cardPai.ehCardPai) {
    return {
      permitido: false,
      motivo: 'O card de destino não é um card pai'
    };
  }

  // Verificar se o card filho já tem um pai
  if (cardFilho.cardPaiId) {
    return {
      permitido: false,
      motivo: 'Card já possui um card pai'
    };
  }

  // Verificar se o card filho é ele mesmo um pai
  if (cardFilho.ehCardPai) {
    return {
      permitido: false,
      motivo: 'Cards pai não podem ser filhos de outros cards'
    };
  }

  // Verificar etapas proibidas para filhos
  const regras = etapaPai.configuracaoHierarquia.regraAgrupamento;
  if (regras.etapasProibidasParaFilhos?.includes(cardFilho.etapaId)) {
    return {
      permitido: false,
      motivo: 'Cards desta etapa não podem se tornar filhos'
    };
  }

  // NOVA VALIDAÇÃO: Verificar se existem automações ativas na etapa do filho
  if (automacoes && automacoes.length > 0) {
    console.log('🔍 [DEBUG] Total de automações recebidas:', automacoes.length);
    console.log('🔍 [DEBUG] Etapa do filho:', cardFilho.etapaId);
    console.log('🔍 [DEBUG] Etapa do pai:', cardPai.etapaId);
    
    const automacoesNaEtapaFilho = automacoes.filter(automacao => {
      // CORREÇÃO: Usar o nome correto do campo MongoDB
      const etapaOrigemId = automacao.etapaOrigemId;
      console.log('🔍 [DEBUG] Verificando automação:', {
        nome: automacao.nome,
        etapaOrigemId: automacao.etapaOrigemId,
        flowId: automacao.flowId,
        ativa: automacao.ativa,
        cardFilhoEtapaId: cardFilho.etapaId
      });
      
      if (!automacao.ativa && automacao.ativa !== undefined) return false;
      
      // Automação tem origem na etapa do filho
      if (String(etapaOrigemId) === String(cardFilho.etapaId)) {
        console.log('✅ [DEBUG] Automação encontrada na etapa do filho!');
        return true;
      }
      
      // Automação move cards PARA a etapa do filho
      if (automacao.acoes?.some((acao: any) => 
        acao.tipo === 'mover_card' && String(acao.etapaDestino) === String(cardFilho.etapaId)
      )) {
        console.log('✅ [DEBUG] Automação move cards PARA a etapa do filho!');
        return true;
      }
      
      return false;
    });

    console.log('🔍 [Validação Filho] Etapa do filho:', cardFilho.etapaId, etapaFilho.nome);
    console.log('🔍 [Validação Filho] Automações na etapa:', automacoesNaEtapaFilho);
    console.log('🔍 [Validação Filho] Cards com exceção configurados:', regras.cardsExcecaoAutomacao);

    // Se existem automações, verificar se card está na lista de exceções
    if (automacoesNaEtapaFilho.length > 0) {
      // Verificar se este card específico está autorizado como exceção
      const cardsExcecao = regras.cardsExcecaoAutomacao || [];
      const cardEhExcecao = cardsExcecao.includes(cardFilho.id);
      
      console.log('🔍 [Validação Filho] Card é exceção?', cardEhExcecao);

      if (!cardEhExcecao) {
        // Card NÃO está na lista de exceções, bloquear
        const nomesAutomacoes = automacoesNaEtapaFilho.map(a => `• ${a.nome}`).join('\n');
        return {
          permitido: false,
          motivo: `⚠️ Este card possui ${automacoesNaEtapaFilho.length} automação(ões) ativa(s) na etapa "${etapaFilho.nome}":\n\n${nomesAutomacoes}\n\nPara permitir que este card específico vire filho, você precisa autorizá-lo como exceção.`,
          precisaConfigurar: true,
          cardId: cardFilho.id,
          automacoes: automacoesNaEtapaFilho
        };
      }
      
      // Card está na lista de exceções, permitir
      console.log('✅ [Validação Filho] Card autorizado como exceção - permitindo transformação');
    }
  }

  // Verificar limite de filhos
  const totalFilhosAtuais = cardPai.cardsFilhosIds?.length || 0;
  if (regras.limiteFilhos && totalFilhosAtuais >= regras.limiteFilhos) {
    return {
      permitido: false,
      motivo: `Card pai já atingiu o limite de ${regras.limiteFilhos} filhos`
    };
  }

  // Verificar condições baseadas em campos do card filho
  if (regras.condicoesFilho && regras.condicoesFilho.length > 0) {
    for (const condicao of regras.condicoesFilho) {
      const valorCampo = cardFilho.campos[condicao.campoId];
      const validacao = validarCondicao(valorCampo, condicao.operador, condicao.valor);
      
      if (!validacao) {
        return {
          permitido: false,
          motivo: `Condição do campo filho não atendida`
        };
      }
    }
  }

  return { permitido: true };
};

/**
 * Transforma um card normal em card pai
 */
export const transformarEmCardPai = (card: Card): Card => {
  return {
    ...card,
    ehCardPai: true,
    cardsFilhosIds: [],
    cardsFilhos: []
  };
};

/**
 * Adiciona um card filho a um card pai
 */
export const adicionarCardFilho = (
  cardPai: Card,
  cardFilho: Card
): { pai: Card; filho: Card } => {
  const novoFilhosIds = [...(cardPai.cardsFilhosIds || []), cardFilho.id];
  
  return {
    pai: {
      ...cardPai,
      cardsFilhosIds: novoFilhosIds,
      cardsFilhos: [...(cardPai.cardsFilhos || []), cardFilho]
    },
    filho: {
      ...cardFilho,
      cardPaiId: cardPai.id
    }
  };
};

/**
 * Remove um card filho de um card pai
 */
export const removerCardFilho = (
  cardPai: Card,
  cardFilhoId: string
): { pai: Card; filhoId: string } => {
  const novosFilhosIds = (cardPai.cardsFilhosIds || []).filter(id => id !== cardFilhoId);
  const novosFilhos = (cardPai.cardsFilhos || []).filter(filho => filho.id !== cardFilhoId);
  
  return {
    pai: {
      ...cardPai,
      cardsFilhosIds: novosFilhosIds,
      cardsFilhos: novosFilhos
    },
    filhoId: cardFilhoId
  };
};

/**
 * Verifica se um card pai pode ser destravado (transformado de volta em card normal)
 */
export const podeDestravarCardPai = (
  cardPai: Card
): { permitido: boolean; motivo?: string } => {
  // Verificar se o card é realmente um pai
  if (!cardPai.ehCardPai) {
    return {
      permitido: false,
      motivo: 'Este card não é um card pai'
    };
  }

  // Verificar se tem filhos agrupados
  const temFilhos = (cardPai.cardsFilhosIds && cardPai.cardsFilhosIds.length > 0) ||
                    (cardPai.cardsFilhos && cardPai.cardsFilhos.length > 0);
  
  if (temFilhos) {
    return {
      permitido: false,
      motivo: 'Card pai possui filhos agrupados. Remova todos os filhos antes de destravar.'
    };
  }

  return { permitido: true };
};

/**
 * Destravar um card pai (transformar de volta em card normal)
 */
export const destravarCardPai = (cardPai: Card): Card => {
  return {
    ...cardPai,
    ehCardPai: false,
    cardsFilhosIds: [],
    cardsFilhos: []
  };
};

/**
 * Popula os cards filhos em um card pai com base nos IDs
 */
export const popularCardsFilhos = (cardPai: Card, todosCards: Card[]): Card => {
  if (!cardPai.ehCardPai || !cardPai.cardsFilhosIds) {
    return cardPai;
  }

  const filhos = cardPai.cardsFilhosIds
    .map(id => todosCards.find(c => String(c.id) === String(id)))
    .filter((c): c is Card => c !== undefined);

  return {
    ...cardPai,
    cardsFilhos: filhos
  };
};

/**
 * Valida uma condição baseada em um operador
 */
function validarCondicao(
  valor: any,
  operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor',
  valorEsperado: any
): boolean {
  switch (operador) {
    case 'igual':
      return valor === valorEsperado;
    case 'diferente':
      return valor !== valorEsperado;
    case 'contem':
      return String(valor).toLowerCase().includes(String(valorEsperado).toLowerCase());
    case 'nao_contem':
      return !String(valor).toLowerCase().includes(String(valorEsperado).toLowerCase());
    case 'maior':
      return Number(valor) > Number(valorEsperado);
    case 'menor':
      return Number(valor) < Number(valorEsperado);
    default:
      return false;
  }
}

/**
 * Obtém todos os cards de um flow, populando a hierarquia
 */
export const obterCardsComHierarquia = (flow: Flow): Card[] => {
  const cards = flow.cards || [];
  
  // Primeiro, popular os cards filhos em todos os pais
  return cards.map(card => {
    if (card.ehCardPai) {
      return popularCardsFilhos(card, cards);
    }
    return card;
  });
};

/**
 * Obtém apenas os cards que não são filhos (para exibição no Kanban)
 */
export const obterCardsRaiz = (cards: Card[]): Card[] => {
  return cards.filter(card => !card.cardPaiId);
};
